import { useEffect, useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ID_ESTADO_EN_ESPERA,
  ID_ESTADO_NO_CONFIRMADO,
  ID_ESTADO_MONTOS_NO_OK,
  ID_ESTADO_MONTOS_OK
} from '../../constants/pedidoEstados'
import { toastErrorLog } from '../../helpers/toastErrorLog'

import * as pedidosService from '../servicios/pedidosService'
import BordeDetalle from '../ui/BordeDetalle'
import { DetalleEstadoPedido } from './DetalleEstadoPedido'
import styles from './GestionEnvio.module.css'
import { SelectorItemsPedido } from './SelectorItemsPedido'

const GestionEnvio = () => {
  const { idPedido } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()

  const [pedidoItems, setPedidoItems] = useState([])
  const [estados, setEstados] = useState([])
  const [seleccionados, setSeleccionados] = useState([])
  const [errorSinSeleccion, setErrorSinSeleccion] = useState(false)
  const [estadoElegido, setEstadoElegido] = useState(0)

  const cargar = useCallback(async () => {
    try {
      const respuesta = await pedidosService.buscarPorIdPedido(idPedido)
      const data = respuesta?.data
      if (!data?.pedidoItems?.length) {
        setPedidoItems([])
        setEstados([])
        setSeleccionados([])
        toastErrorLog(null, `No se encontró el pedido Nº ${idPedido} o fue eliminado.`, 'pedidos')
        navigate('/Ventas', { replace: true })
        return
      }
      const items = data.pedidoItems.map((item) => ({
        ...item,
        fechaPedido: data.fechaPedido,
        idComprador: data.idComprador,
        idPedido: data.idPedido,
        precioEnvio: item.precioEnvio ?? data.precioEnvio ?? 0,
        envioTipo: item.envioTipo ?? data.envioTipo ?? '',
        idMedioEnvio: item.idMedioEnvio ?? data.idMedioEnvio ?? null
      }))
      setPedidoItems(items)
      setSeleccionados(items.map((item) => item.idPedidoItem))

      if (items[0]) {
        const detalle = await pedidosService.buscarPorPedidoItem(items[0].idPedidoItem)
        if (!detalle) {
          setPedidoItems([])
          setEstados([])
          setSeleccionados([])
          toastErrorLog(null, `No se encontró el pedido Nº ${idPedido} o fue eliminado.`, 'pedidos')
          navigate('/Ventas', { replace: true })
          return
        }
        setEstados(detalle.data?.estados ?? [])
      }
    } catch {
      setPedidoItems([])
      setEstados([])
      setSeleccionados([])
    }
  }, [idPedido])
  const { activos, bloqueados, itemsParaGuardar, itemConEstadoMaximo } = useMemo(() => {
    const activos = []
    const bloqueados = []
    const itemsParaGuardar = []
    let itemConEstadoMaximo = null

    for (const item of pedidoItems) {
      const seleccionado = seleccionados.includes(item.idPedidoItem)
      const estadoEfectivo = estados.find((e) => e.idPedidoItemEstado === item.idPedidoItemEstado)
        ?.esAutomatico
        ? 0
        : (item.idPedidoItemEstado ?? 0)
      const bloqueado = estadoElegido > 0 && estadoEfectivo >= estadoElegido

      if (bloqueado) bloqueados.push(item.idPedidoItem)
      if (seleccionado && !bloqueado) {
        activos.push(item)
        itemsParaGuardar.push(item)
        if (
          !itemConEstadoMaximo ||
          (item.idPedidoItemEstado ?? 0) > (itemConEstadoMaximo.idPedidoItemEstado ?? 0)
        ) {
          itemConEstadoMaximo = item
        }
      } else if (seleccionado) {
        activos.push(item)
      }
    }

    return { activos, bloqueados, itemsParaGuardar, itemConEstadoMaximo }
  }, [pedidoItems, seleccionados, estadoElegido, estados])

  useEffect(() => {
    cargar()
  }, [cargar])

  const toggleSeleccion = (id) => {
    setErrorSinSeleccion(false)
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    )
  }
  const deshabilitarCambio =
    state.pedido.pedidoItems[0].idPedidoItemEstado === ID_ESTADO_MONTOS_NO_OK ||
    state.pedido.pedidoItems[0].idPedidoItemEstado === ID_ESTADO_MONTOS_OK ||
    state.pedido.pedidoItems[0].idPedidoItemEstado === ID_ESTADO_EN_ESPERA ||
    state.pedido.pedidoItems[0].idPedidoItemEstado === ID_ESTADO_NO_CONFIRMADO

  const estadoMinimoPermitido = useMemo(() => {
    const esAutomatico = estados.find(
      (e) => e.idPedidoItemEstado === itemConEstadoMaximo?.idPedidoItemEstado
    )?.esAutomatico
    return {
      estado: esAutomatico ? 0 : (itemConEstadoMaximo?.idPedidoItemEstado ?? 0),
      fechaDespachado: esAutomatico ? null : (itemConEstadoMaximo?.fechaDespachado ?? null),
      fechaRecibido: esAutomatico ? null : (itemConEstadoMaximo?.fechaRecibido ?? null)
    }
  }, [estados, itemConEstadoMaximo])

  const pedidoRef = useMemo(() => {
    if (activos.length === 0) return pedidoItems[0] ?? null
    return activos.reduce(
      (min, item) => ((item.idPedidoItemEstado ?? 0) < (min.idPedidoItemEstado ?? 0) ? item : min),
      activos[0]
    )
  }, [activos, pedidoItems])
  if (!state?.pedido) return null

  const toggleSeleccionarTodos = () => {
    const desbloqueados = pedidoItems
      .filter((item) => !bloqueados.includes(item.idPedidoItem))
      .map((item) => item.idPedidoItem)

    const todosSeleccionados = desbloqueados.every((id) => seleccionados.includes(id))

    if (todosSeleccionados) {
      setSeleccionados((prev) => prev.filter((id) => bloqueados.includes(id)))
    } else {
      setSeleccionados((prev) => [
        ...new Set([...prev.filter((id) => bloqueados.includes(id)), ...desbloqueados])
      ])
    }
    setErrorSinSeleccion(false)
  }

  if (!pedidoRef) return null

  const esItemUnico = pedidoItems.length === 1

  return (
    <BordeDetalle
      titulo={`Gestión de envío — Pedido Nº ${idPedido}`}
      onCerrarClick={() => navigate(-1)}
      className={styles.contenedor}
    >
      <div className={styles.layout}>
        <div className={styles.panelSelector}>
          <SelectorItemsPedido
            items={pedidoItems}
            seleccionados={seleccionados}
            onToggle={toggleSeleccion}
            errorSinSeleccion={errorSinSeleccion}
            bloqueados={bloqueados}
            onSeleccionarTodos={toggleSeleccionarTodos}
          />
        </div>
        <div className={styles.panelDetalle}>
          <DetalleEstadoPedido
            pedido={pedidoRef}
            estados={estados}
            onActualizar={cargar}
            itemsSeleccionados={esItemUnico ? null : itemsParaGuardar}
            onGuardarBloqueado={() => setErrorSinSeleccion(true)}
            onEstadoChange={setEstadoElegido}
            disabled={deshabilitarCambio}
            estadoMinimoPermitido={estadoMinimoPermitido}
            esItemUnico={esItemUnico}
            gestionEnvio
          />
        </div>
      </div>
    </BordeDetalle>
  )
}

export default GestionEnvio
